import React, { FC } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";

import Header from "../../components/Header";
import Page from "../../components/Page";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { useSelector } from "../../store";
import { useListChangesetsQuery } from "../../store/enhancedApi";
import { Changeset, useCompareChangesetsQuery } from "../../store/pomeriumApi";
import ActivityLogDiff from "./components/ActivityLogDiff";

type ChangesetPickerProps = {
  allowEmpty: boolean;
  selectedId?: string;
  changesets?: Changeset[];
  onChange: (evt: SelectChangeEvent<string>) => void;
};
const ChangesetPicker: FC<ChangesetPickerProps> = ({
  allowEmpty,
  selectedId,
  changesets,
  onChange,
}) => {
  const items =
    changesets?.map((c) => ({
      value: c.id,
      title: `${c.updatedAt} | ${c.id}`,
    })) || [];
  if (allowEmpty) {
    items.splice(0, 0, {
      value: "",
      title: "",
    });
  }
  if (!items?.find((i) => i.value === selectedId)) {
    items.push({
      value: selectedId || "",
      title: selectedId || "",
    });
  }
  return (
    <Select fullWidth value={selectedId || ""} onChange={onChange}>
      {items.map((i) => (
        <MenuItem key={i.value} value={i.value}>
          {i.title}
        </MenuItem>
      ))}
    </Select>
  );
};

const Compare: FC = () => {
  const navigate = useNavigateWithSearch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const startChangesetId = searchParams.get("to") || undefined;
  const endChangesetId = params.changesetId;
  const { clusterId, organizationId } = useSelector((state) => state.current);

  const { data: result } = useListChangesetsQuery(
    {
      organizationId,
      clusterId,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const changesets = result?.rows || [];
  const { data: comparison } = useCompareChangesetsQuery(
    {
      organizationId,
      clusterId,
      firstId: startChangesetId,
      secondId: endChangesetId,
    },
    {
      skip: !organizationId || !clusterId || !endChangesetId,
    },
  );

  function handleChangeStartChangeset(evt: SelectChangeEvent<string>) {
    setSearchParams(
      (params) => {
        params.set("to", evt.target.value);
        return params;
      },
      { replace: true },
    );
  }

  function handleChangeEndChangeset(evt: SelectChangeEvent<string>) {
    navigate(`/app/reports/deployments/${evt.target.value}/compare`);
  }

  return (
    <Page title="Change Set Changes">
      <Stack spacing={1} sx={{ height: "100%" }}>
        <Header title="Change Set Changes" breadcrumbs={true} />

        <Stack direction="row" spacing={1}>
          <ChangesetPicker
            allowEmpty={true}
            changesets={changesets}
            selectedId={comparison?.startChangeset?.id}
            onChange={handleChangeStartChangeset}
          />
          <ChangesetPicker
            allowEmpty={false}
            changesets={changesets}
            selectedId={endChangesetId}
            onChange={handleChangeEndChangeset}
          />
        </Stack>
        {comparison?.entities?.map((entity, i) => (
          <ActivityLogDiff
            key={i}
            left={entity.length === 1 ? undefined : entity[0]}
            right={entity.length === 1 ? entity[0] : entity[1]}
          />
        ))}
      </Stack>
    </Page>
  );
};
export default Compare;
